import { IconName, type AgGridMenuItems, type LedgerAccountTypeEnum, type MixpanelEventSource } from '@talos/kyoko';
import { useCallback } from 'react';
import { usePositionDetailsModal } from '../../../../providers/PositionDetailsModalProvider';
import { useNavigateToAccountLedgerEvents } from './useNavigateToAccountLedgerEvents';

/** A hook returning a function to call which returns AgGrid menu items for navigating to account and position ledger event pages */
export function useGetOpenTransactionsMenuItems() {
  const navigateToAccountLedgerEvents = useNavigateToAccountLedgerEvents();
  const { open: openPositionDetailsModal } = usePositionDetailsModal();

  return useCallback(
    ({
      account,
      asset,
      accountType,
      source,
      showAccountOption = false,
      name,
      inModal = false,
      subAccountLabel = 'Sub Account',
    }: {
      account: string | undefined;
      asset?: string;
      accountType: LedgerAccountTypeEnum;
      /** If true, will add a _second_ option to open the Account Ledger Events page for the entire account (asset is unused) */
      showAccountOption?: boolean;
      source?: MixpanelEventSource;
      name?: string;
      /** Set to true to open in a modal. Otherwise, will route to Trading/AccountLedgerEvents */
      inModal?: boolean;
      /** Label to use for SubAccount type. Can be 'Sub Account', 'Rollup', or 'Portfolio'. Defaults to 'Sub Account' */
      subAccountLabel?: 'Sub Account' | 'Rollup' | 'Portfolio';
    }): AgGridMenuItems => {
      const items: AgGridMenuItems = [];
      const openFn = inModal ? openPositionDetailsModal : navigateToAccountLedgerEvents;

      if (account != null && accountType != null && asset != null) {
        items.push({
          name: name ?? (showAccountOption ? `Open Instrument Transactions` : `Open Transactions`),
          action: () => {
            openFn({
              account,
              asset,
              accountType,
              source,
            });
          },
          icon: `<i class="ag-icon ${IconName.ViewListDrilldown}"/>`,
        });
      }

      if (
        showAccountOption &&
        account != null &&
        accountType != null &&
        ['MarketAccount', 'SubAccount'].includes(accountType)
      ) {
        const accountTypeLabel = accountType === 'MarketAccount' ? 'Market Account' : subAccountLabel;
        items.push({
          name: name ?? `Open ${accountTypeLabel} Transactions`,
          action: () => {
            openFn({
              account,
              accountType,
              source,
              asset: undefined,
            });
          },
          icon: `<i class="ag-icon ${IconName.ViewListDrilldown}"/>`,
        });
      }

      return items;
    },
    [navigateToAccountLedgerEvents, openPositionDetailsModal]
  );
}
